import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CameraIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { PROFILE_ROUTE } from '../../../routes';
import {
  trackProfilePhotoNudgeShown,
  trackProfilePhotoNudgeDismissed,
} from '../../../services/analytics';

interface ProfilePhotoNudgePromptProps {
  /** Where this prompt is shown, e.g. "event_subscribe" | "first_login" | "first_chat_message". Forwarded to analytics. */
  touchpoint: string;
  isOpen: boolean;
  onClose: () => void;
}

const BENEFITS = [
  'Aiuta le altre persone a riconoscerti agli eventi',
  'Rende il tuo profilo più autentico e affidabile',
  'Favorisce le connessioni prima ancora di incontrarvi',
];

const ProfilePhotoNudgePrompt: React.FC<ProfilePhotoNudgePromptProps> = ({
  touchpoint,
  isOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isOpen) {
      trackProfilePhotoNudgeShown(touchpoint);
    }
  }, [isOpen, touchpoint]);

  if (!isOpen) return null;

  const handleDismiss = () => {
    trackProfilePhotoNudgeDismissed(touchpoint);
    onClose();
  };

  const handleUploadClick = () => {
    onClose();
    navigate(
      `${PROFILE_ROUTE}?edit=true&redirectTo=${encodeURIComponent(
        location.pathname
      )}`
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-auto overflow-hidden">
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>

        <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-pink-600 px-6 pt-8 pb-6 text-white">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-white bg-opacity-20 rounded-full p-3">
              <CameraIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-center mb-2">
            Una foto per il tuo profilo?
          </h3>
          <p className="text-orange-50 text-center leading-relaxed">
            Il tuo profilo ha ancora la foto predefinita. Aggiungi una tua
            foto: fa la differenza!
          </p>
        </div>

        <div className="px-6 py-6">
          <div className="space-y-3">
            {BENEFITS.map((benefit, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="bg-green-100 rounded-full p-1">
                    <CheckIcon className="h-4 w-4 text-green-600" />
                  </div>
                </div>
                <span className="text-gray-700 font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 pb-6">
          <div className="space-y-3">
            <button
              onClick={handleUploadClick}
              className="w-full bg-gradient-to-r from-amber-500 to-pink-600 text-white font-semibold py-3 px-4 rounded-xl hover:from-amber-600 hover:to-pink-700 transition-all duration-200 transform hover:scale-105 shadow-lg text-center block"
            >
              Carica una foto
            </button>
            <button
              onClick={handleDismiss}
              className="w-full bg-gray-100 text-gray-700 font-medium py-3 px-4 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Continua senza foto
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePhotoNudgePrompt;
